import { cloneDeep } from 'lodash';
import { v4 as uuid } from 'uuid';
import { Message } from '@meitu/saas-ui';
import { createCanvas, penBridge } from '@meitu/wasm-lib';
import { getFramework, IFramework, mergeMaterialIds } from '@/framework';
import { Result, Stage } from '@/framework/plugins';
import { getLocaleStore } from '@/i18n';
import { type MainLayer } from '@/layers/main-layer';
import { checkNeedMergeCanvas } from '@/layers/main-layer/utils';
import { renderLayerToStage } from '@/layers/main-layer/views/render-to-stage';
import { base64Kit } from '@/utils/base64-kit';
import { loadImageAsync } from '@/utils/file-kit';
import { getCanvasById } from '@/utils/layer-kit';
import { uploadBase64ToObs } from '@/utils/upload-kit';
import { IImageLayerProps, LayerType } from '@/version-types';
import { isHttpUri } from '../../utils/file-kit';
import { getDrawPenSVG } from './components/draw-pen-path';
import { getFailedText } from './constants';
import {
    getEliminatePenState,
    IStore,
    watchEliminatePenConfig,
    watchEliminatePenState,
} from './states/eliminate-pen-state';
import {
    ELIMINATE_PEN_EVENT,
    EliminateConfig,
    EliminatePenOptions,
    EliminateType,
    IDrawInfo,
    IPathInfo,
    RecognizeTarget,
} from './types';
import {
    createMaskFromSVG,
    createPathInfoWithPointConvert,
    getExtendState,
    isAIEliminateUsed,
    updateExtendState,
    updateMaterialIds,
} from './utils';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * 给 Plugin View 使用
 */
export class EliminatePenController {
    private framework: IFramework;
    private store: IStore;
    public options: EliminatePenOptions = {};
    /** 确认消除完成后的所有功能使用埋点列表 */
    public confirmTrackFuncList: string[] = [];
    /** 应用一次消除过程的所有功能使用埋点列表 */
    public applyTrackFuncList: string[] = [];
    /** 当前quickstart面板开启选项对应的Func */
    public recognizeFunc: string = '';
    /** 商业化功能 */
    public commercialFunc: string = '';

    constructor(identity: string) {
        this.framework = getFramework(identity);
        this.store = getEliminatePenState(this.framework.identity);

        watchEliminatePenState(identity, (store) => {
            this.store = store;
        });
    }

    private tryGetMainLayer() {
        const mainLayer = this.framework.getInstance<MainLayer>('MainLayer');

        if (!mainLayer) {
            throw new Error('MainLayer is not found');
        }

        return mainLayer;
    }

    getScope() {
        return this.tryGetMainLayer().scope;
    }

    getMergeLayerType() {
        return this.tryGetMainLayer().mergeLayerType;
    }

    setCommercialFunc(commercialFunc: string) {
        this.commercialFunc = commercialFunc;
    }

    setOptions(options: EliminatePenOptions) {
        this.options = options;
    }

    updateLayerPathInfos(newPathInfos: IDrawInfo[], writeHistory = false) {
        const layerId = this.store.originLayer?.id;
        const oldLayer = cloneDeep(this.framework.getLayer(layerId!));
        const newLayer = cloneDeep(oldLayer);

        if (!layerId || !newLayer) return;

        updateExtendState(newLayer, newPathInfos);
        this.framework.stage.addOrUpdate(newLayer);

        if (writeHistory) {
            this.framework.history.write(oldLayer, newLayer);
        }
    }

    updatePathInfos(newPathInfos: IDrawInfo[]) {
        this.updateLayerPathInfos(newPathInfos, this.options.isSelectionSaveToHistory);
    }

    private async doEliminate(eliminateMask: HTMLCanvasElement) {
        const { framework, store } = this;

        const originIndex = framework.stage.getActiveOriginIndex();
        const mainLayer = framework.getMainLayer(originIndex);
        const originLayer = store.originLayer;
        let layers = framework.getLayers(originIndex);

        if (this.getScope() === 'canvas' && this.getMergeLayerType().length > 0) {
            layers = layers.filter((layer) => this.getMergeLayerType().includes(layer.layerType));
        }

        const { type, recognizeInfo } = store.eliminateConfig;

        if (!originLayer || !mainLayer) return;

        const oldLayer = cloneDeep(mainLayer);
        const newLayer = cloneDeep(oldLayer);

        // AI 消除走外部处理
        if (type === EliminateType.AI) {
            const aiEliminateHandler = this.options.aiEliminateHandler;

            if (!aiEliminateHandler) {
                throw new Error('aiEliminateHandler is not set');
            }
            let maskURL = eliminateMask.toDataURL('image/png');

            let originalImageURL = '';

            // ATTENTION: 当前消除笔仅支持主图，所以不用单独判断
            if (checkNeedMergeCanvas(this.getScope(), layers)) {
                const canvasImage = await this.framework.getInstance(Result).drawLayers(layers, 'png');
                const base64 = canvasImage.toDataURL('image/png');
                const result = await uploadBase64ToObs(base64);

                originalImageURL = result.url;

                // 合并分离图层 materialIds
                let materialIds: Record<string, string[]> = {};

                layers.forEach((layer) => {
                    // 合并所有图层的 materialIds 到主图
                    materialIds = mergeMaterialIds(materialIds, layer.materialIds);
                });

                newLayer.materialIds = materialIds;
                // 合成画布后， mask 也要基于合成画布重新绘制，这里直接把 mask 图用原来的主图的信息去裁剪旋转等处理
                const maskCanvasImage = await renderLayerToStage(this.framework.identity, newLayer, eliminateMask);

                maskURL = maskCanvasImage.toDataURL('image/png');
            } else {
                originalImageURL = await base64Kit.getRemoteUrlAsync((oldLayer?.data as IImageLayerProps).url);
            }

            console.log('originalImageURL==========', originalImageURL);
            let { url: eliminatedImageURL } = await aiEliminateHandler(
                originalImageURL,
                maskURL,
                recognizeInfo,
                this.options.isManualCrop,
            );

            // 当地址是 base64，上传到 base64Kit
            if (!isHttpUri(eliminatedImageURL)) {
                eliminatedImageURL = base64Kit.getCode(eliminatedImageURL, uuid());
            }

            // 先加载图片，避免 loading 先关闭才会显示结果图
            await loadImageAsync(eliminatedImageURL);
            // 更新图层数据
            (newLayer.data as IImageLayerProps).url = eliminatedImageURL;
            newLayer.isAILayer = true;
        } else {
            // ATTENTION: 手动消除当前还不支持 scope 为 canvas 的情况
            const { canvas, ctx, width, height } = getCanvasById(originLayer.id);
            const fromCanvas = createCanvas(width, height, canvas);
            // 本地消除走 ImageKit 处理
            const res = await penBridge.from(fromCanvas).eliminate(eliminateMask);

            if (!res.canvas) return;

            // 直接回显到页面
            ctx?.clearRect(0, 0, width, height);
            ctx?.drawImage(res.canvas, 0, 0);

            // 更新图层数据
            const base64 = getCanvasById(newLayer.id).canvas.toDataURL();

            (newLayer.data as IImageLayerProps).url = base64Kit.getCode(base64, uuid());
        }

        updateMaterialIds(newLayer, type, this.commercialFunc);
        // 消除完成后，需要清空已经绘制的选区
        updateExtendState(newLayer, []);

        const trackFunc = this.applyTrackFuncList.join(',');

        if (checkNeedMergeCanvas(this.getScope(), layers)) {
            // 画布模式下，需要替换除背景的所有的图层
            const layers = framework
                .getLayers(originIndex)
                .filter((layer) => this.getMergeLayerType().includes(layer.layerType));
            const replaceIds = layers.filter((layer) => layer.layerType !== 'bg').map((layer) => layer.id);

            const imageLayerData = newLayer.data as IImageLayerProps;
            // ATTENTION: 合成之后需要把主图改成非透明
            imageLayerData.isTransparent = false;
            imageLayerData.imageTransform = {
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                scale: 1,
                flipH: false,
                flipV: false,
                widthRatio: 1,
                heightRatio: 1,
            };
            imageLayerData.naturalHeight = newLayer.height;
            imageLayerData.naturalWidth = newLayer.width;
            newLayer.rotate = 0;
            newLayer.left = 0;
            newLayer.top = 0;
            this.framework.getInstance(Stage).replaceLayer(replaceIds, newLayer, undefined, trackFunc);
        } else {
            framework.stage.addOrUpdate(newLayer);
            framework.history.write(oldLayer, newLayer, trackFunc);
        }
    }

    async eliminate(pathInfos: IPathInfo[]) {
        const { $t } = getLocaleStore();
        const { framework, store } = this;
        const originLayer = store.originLayer;
        const eliminateConfig = store.eliminateConfig;
        const {
            type,
            watermarkMaskURL,
            textMaskURL,
            autoRecognizeWatermark,
            autoRecognizeText,
            isWatermarkMaskUsed,
            isTextMaskUsed,
        } = eliminateConfig;

        // 消除都是拿原图去消除，所以创建的蒙版也是要按照原图的尺寸
        const size = {
            width:
                ((originLayer?.data as IImageLayerProps)?.naturalWidth || 0) *
                (originLayer?.data as IImageLayerProps)?.imageTransform.scale,
            height:
                ((originLayer?.data as IImageLayerProps)?.naturalHeight || 0) *
                (originLayer?.data as IImageLayerProps)?.imageTransform.scale,
        };
        const svg = getDrawPenSVG(
            size,
            pathInfos,
            type,
            autoRecognizeWatermark && !isWatermarkMaskUsed ? watermarkMaskURL : '',
            autoRecognizeText && !isTextMaskUsed ? textMaskURL : '',
        );
        const canvas = await createMaskFromSVG(svg, size);

        if (!canvas) return;
        const resultCanvas = createCanvas(
            (originLayer?.data as IImageLayerProps)?.naturalWidth || 0,
            (originLayer?.data as IImageLayerProps)?.naturalHeight || 0,
        );
        const resultCtx = resultCanvas.getContext('2d');
        resultCtx?.drawImage(canvas, 0, 0, size.width, size.height, 0, 0, resultCanvas.width, resultCanvas.height);

        framework.operate.globalMask.show({
            loading: true,
            text: $t((v) => v.mainLayer.operate.handleMessage),
        });
        framework.events.emit(ELIMINATE_PEN_EVENT.LOADING, true);

        // 让loading先弹出，在执行消除
        await delay(30);

        try {
            await this.doEliminate(resultCanvas);

            if (type === EliminateType.AI) {
                if (autoRecognizeWatermark && !isWatermarkMaskUsed) {
                    this.store.setEliminateConfig({ isWatermarkMaskUsed: true });
                }

                if (autoRecognizeText && !isTextMaskUsed) {
                    this.store.setEliminateConfig({ isTextMaskUsed: true });
                }
            }
        } catch (e: any) {
            console.error(e);
            if (e.data?.code === 10025) {
                // 入参审核不通过
                Message.warn($t((v) => v.mainLayer.operate.inputAuditFailedTips));
                return;
            }
            const errorType = (e as unknown as null | { name: string })?.name;

            // HACK: 当前 core 不想依赖 ai api sdk ，所以这里直接写死错误值
            if (errorType === 'EraserNoMaskError') {
                Message.warn($t((v) => v.mainLayer.operate.emptyAreaTips));
            } else if (errorType === 'AuditError') {
                // 消除结果审核不通过
                Message.warn($t((v) => v.mainLayer.operate.auditFailedTips));
            } else {
                Message.error($t((v) => v.mainLayer.operate.errorMessage));
            }

            throw new Error(`eliminate error: ${errorType}`);
        } finally {
            framework.operate.globalMask.hide();
            framework.events.emit(ELIMINATE_PEN_EVENT.APPLY_END);
            framework.events.emit(ELIMINATE_PEN_EVENT.LOADING, false);
        }
    }

    // TODO: @cxz1 需要抽离公共的图层处理代码
    private async doQuickEliminate(target: RecognizeTarget) {
        const { framework, store } = this;
        const originIndex = framework.stage.getActiveOriginIndex();
        const mainLayer = framework.getMainLayer(originIndex);
        const originLayer = store.originLayer;
        const { type } = store.eliminateConfig;

        if (!originLayer || !mainLayer) return;

        const oldLayer = cloneDeep(mainLayer);
        const newLayer = cloneDeep(oldLayer);

        const originalImageURL = (oldLayer?.data as IImageLayerProps).url;
        let layers = this.framework.getLayers(originIndex);

        if (this.getScope() === 'canvas' && this.getMergeLayerType().length > 0) {
            layers = layers.filter((layer) => this.getMergeLayerType().includes(layer.layerType));
        }

        let oldURL = '';

        if (checkNeedMergeCanvas(this.getScope(), layers)) {
            const canvasImage = await this.framework.getInstance(Result).drawLayers(layers, 'png');

            // TODO: @cxz1 检查当前是否是透明度
            const base64 = canvasImage.toDataURL('image/png');
            const result = await uploadBase64ToObs(base64);

            oldURL = result.url;
        } else {
            oldURL = await base64Kit.getRemoteUrlAsync(originalImageURL);
        }

        const recognizeAndEliminateHandler = this.options.recognizeAndEliminateHandler;

        if (!recognizeAndEliminateHandler) {
            throw new Error('recognizeAndEliminateHandler is not set');
        }
        const res = await recognizeAndEliminateHandler(oldURL, target).catch((e) => {
            this.setAutoRecognize(target, false);
            this.recognizeFunc = '';
            throw e;
        });
        const newURL = res.url;

        if (this.recognizeFunc) {
            this.applyTrackFuncList.push(this.recognizeFunc);
        }

        // 先加载图片，避免 loading 先关闭才会显示结果图
        await loadImageAsync(newURL);
        // 更新图层数据
        (newLayer.data as IImageLayerProps).url = newURL;
        // 更新图层以自动识别并消除的类型
        newLayer.extraInfo = {
            ...newLayer.extraInfo,
            eliminateTarget: {
                ...newLayer.extraInfo?.eliminateTarget,
                [target]: true,
            },
        };
        newLayer.isAILayer = true;
        updateMaterialIds(newLayer, type, this.commercialFunc);
        // 消除完成后，需要清空已经绘制的选区
        updateExtendState(newLayer, []);

        const trackFunc = this.applyTrackFuncList.join(',');

        if (checkNeedMergeCanvas(this.getScope(), layers)) {
            // 画布模式下，需要替换除背景的所有的图层
            const layers = framework
                .getLayers(originIndex)
                .filter((layer) => this.getMergeLayerType().includes(layer.layerType));
            const replaceIds = layers.filter((layer) => layer.layerType !== 'bg').map((layer) => layer.id);

            // ATTENTION: 合成之后需要把主图改成非透明
            (newLayer.data as IImageLayerProps).isTransparent = false;
            this.framework.getInstance(Stage).replaceLayer(replaceIds, newLayer, undefined, trackFunc);
        } else {
            framework.stage.addOrUpdate(newLayer);
            framework.history.write(oldLayer, newLayer, trackFunc);
        }
    }

    setAutoRecognize(target: RecognizeTarget, isOpen: boolean) {
        if (target === 'text') {
            this.store.setEliminateConfig({ autoRecognizeText: isOpen });
        } else if (target === 'watermark') {
            this.store.setEliminateConfig({ autoRecognizeWatermark: isOpen });
        } else if (target === 'passerby') {
            this.store.setEliminateConfig({ autoRecognizePasserby: isOpen });
        } else if (target === 'glasses') {
            this.store.setEliminateConfig({ autoRecognizeGlasses: isOpen });
        } else if (target === 'glare') {
            this.store.setEliminateConfig({ autoGlare: isOpen });
        } else if (target === 'logo') {
            this.store.setEliminateConfig({ autoRecognizeLogo: isOpen });
        }
    }

    async quickEliminate(target: RecognizeTarget) {
        const { $t } = getLocaleStore();
        const { framework } = this;

        this.setAutoRecognize(target, true);

        framework.operate.globalMask.show({
            loading: true,
            text: $t((v) => v.mainLayer.operate.handleMessage),
        });
        framework.events.emit(ELIMINATE_PEN_EVENT.LOADING, true);

        // 让loading先弹出，在执行消除
        await delay(30);

        try {
            await this.doQuickEliminate(target);
        } catch (e: unknown) {
            console.error(e);
            const errorType = (e as unknown as null | { name: string })?.name;

            // HACK: 当前 core 不想依赖 ai api sdk ，所以这里直接写死错误值
            if (errorType === 'EraserNoMaskError') {
                const failedText = getFailedText(target);
                Message.warn(failedText);
            } else {
                Message.error($t((v) => v.mainLayer.operate.errorMessage));
            }

            throw new Error(`eliminate error: ${errorType}`);
        } finally {
            framework.operate.globalMask.hide();
            framework.events.emit(ELIMINATE_PEN_EVENT.APPLY_END);
            framework.events.emit(ELIMINATE_PEN_EVENT.LOADING, false);
        }
    }
}

/**
 * 给外部使用的 Plugin Instance
 */
export class EliminatePenPlugin {
    /** 编辑器实例 */
    private framework: IFramework;
    private store: IStore;

    constructor(identity: string) {
        this.framework = getFramework(identity);
        this.store = getEliminatePenState(this.framework.identity);

        watchEliminatePenState(identity, (store) => {
            this.store = store;
        });
    }

    private tryGetMainLayer() {
        const mainLayer = this.framework.getInstance<MainLayer>('MainLayer');

        if (!mainLayer) {
            throw new Error('MainLayer is not found');
        }

        return mainLayer;
    }
    getScope() {
        return this.tryGetMainLayer().scope;
    }

    getMergeLayerType() {
        return this.tryGetMainLayer().mergeLayerType;
    }

    setCommercialFunc(commercialFunc: string) {
        this.framework.getInstance(EliminatePenController).setCommercialFunc(commercialFunc);
    }

    setRecognizeFunc(func: string) {
        this.framework.getInstance(EliminatePenController).recognizeFunc = func;
    }

    setOptions(options: EliminatePenOptions) {
        this.framework.getInstance(EliminatePenController).setOptions(options);
    }

    getConfig() {
        return this.store.eliminateConfig;
    }

    setConfig(config: Partial<EliminateConfig>) {
        this.store.setEliminateConfig(config);
    }

    watchConfig(callback: Parameters<typeof watchEliminatePenConfig>['1']) {
        return watchEliminatePenConfig(this.framework.identity, callback);
    }

    /** 打开消除笔 */
    open(layerId?: string) {
        const originIndex = this.framework.stage.getActiveOriginIndex();
        const originLayer = layerId ? this.framework.getLayer(layerId) : this.framework.getMainLayer(originIndex);

        if (!originLayer) return;

        this.store.setOriginLayer(originLayer);
    }

    /** 关闭消除笔 */
    close() {
        this.framework.getInstance(EliminatePenController).updateLayerPathInfos([]);
        this.store.setOriginLayer(null);
        this.store.destroy();
        this.framework.getInstance(EliminatePenController).confirmTrackFuncList = [];
        this.framework.getInstance(EliminatePenController).applyTrackFuncList = [];
    }

    /** 自动识别 mask */
    async recognize(target: RecognizeTarget, isOpen: boolean) {
        const { $t } = getLocaleStore();
        const originLayer = this.store.originLayer;
        const { watermarkMaskURL, textMaskURL } = this.store.eliminateConfig;

        if (!originLayer) return;

        const layer = this.framework.getLayer(originLayer.id);

        if (!layer) return;

        // 已经识别过的，不需要再重新识别
        if (target === 'watermark') {
            this.store.setEliminateConfig({ autoRecognizeWatermark: isOpen });
            if (watermarkMaskURL) return;
        } else if (target === 'text') {
            this.store.setEliminateConfig({ autoRecognizeText: isOpen });
            if (textMaskURL) return;
        }

        // 未开启自动识别，不需要识别
        if (!isOpen) return;

        this.framework.operate.globalMask.show({
            loading: true,
            text: $t((v) => v.mainLayer.operate.autoRecognizing),
        });
        this.framework.events.emit(ELIMINATE_PEN_EVENT.LOADING, true);

        try {
            const url = (layer.data as IImageLayerProps).url;

            let originalImageURL = '';
            const originIndex = this.framework.stage.getActiveOriginIndex();
            let layers = this.framework.getLayers(originIndex);

            if (this.getScope() === 'canvas' && this.getMergeLayerType().length > 0) {
                layers = layers.filter((layer) => this.getMergeLayerType().includes(layer.layerType));
            }

            if (checkNeedMergeCanvas(this.framework.getInstance(EliminatePenController).getScope(), layers)) {
                const canvasImage = await this.framework.getInstance(Result).drawLayers(layers, 'png');
                // 识别走 jpg 效果和速度会更好
                const base64 = canvasImage.toDataURL('image/jpeg', 0.8);
                const result = await uploadBase64ToObs(base64);

                originalImageURL = result.url;
            } else {
                originalImageURL = await base64Kit.getRemoteUrlAsync(url);
            }

            const result = await this.framework
                .getInstance(EliminatePenController)
                .options.recognizeHandler?.(originalImageURL, target);

            if (!result) return;

            const { url: maskURL, recognizeInfo } = result;

            if (target === 'watermark') {
                this.store.setEliminateConfig({ watermarkMaskURL: maskURL, recognizeInfo });
            } else if (target === 'text') {
                this.store.setEliminateConfig({ textMaskURL: maskURL, recognizeInfo });
            }
        } finally {
            this.framework.operate.globalMask.hide();
            this.framework.events.emit(ELIMINATE_PEN_EVENT.LOADING, false);
        }
    }

    /** 重置消除的图层为原图 */
    reset() {
        const originLayer = this.store.originLayer;

        if (!originLayer) {
            return;
        }

        this.store.setEliminateConfig({
            autoRecognizeWatermark: false,
            autoRecognizeText: false,
            isWatermarkMaskUsed: false,
            isTextMaskUsed: false,
            watermarkMaskURL: '',
            textMaskURL: '',
            recognizeInfo: null,
        });
        this.framework.stage.addOrUpdate(originLayer);
        this.framework.getInstance(EliminatePenController).confirmTrackFuncList = [];
        this.framework.getInstance(EliminatePenController).applyTrackFuncList = [];
    }

    /** 清空选区 */
    clear() {
        // 清空选区同时要清空自动识别，但需要根据是否已经使用过自动识别来决定是否清空
        this.store.setEliminateConfig({
            autoRecognizeWatermark: this.store.eliminateConfig.isWatermarkMaskUsed,
            autoRecognizeText: this.store.eliminateConfig.isTextMaskUsed,
            autoRecognizeGlasses: this.store.eliminateConfig.isGlassesMaskUsed,
            autoRecognizePasserby: this.store.eliminateConfig.isPasserbyMaskUsed,
        });

        this.framework.getInstance(EliminatePenController).updatePathInfos([]);
    }

    /** 执行消除 */
    apply(target?: RecognizeTarget) {
        const layerId = this.store.originLayer?.id;

        if (!layerId) return;

        const layer = this.framework.getLayer(layerId);

        if (!layer) return;

        if (target) {
            this.framework.getInstance(EliminatePenController).quickEliminate(target);
            return;
        }

        const drawInfos = getExtendState(layer);
        const pathInfos = createPathInfoWithPointConvert(layer, drawInfos, true);

        this.framework.getInstance(EliminatePenController).eliminate(pathInfos);
    }

    /** 获取消除编辑的最终数据 */
    getResult() {
        const layerId = this.store.originLayer?.id;

        if (!layerId) return null;

        const trackFunc = this.framework.getInstance(EliminatePenController).confirmTrackFuncList?.join(',');
        const layer = this.framework.getLayer(layerId);
        const oldURL = (this.store.originLayer?.data as IImageLayerProps).url;
        const newURL = (layer.data as IImageLayerProps).url;
        console.log('===>getConfirmResult', { oldURL, newURL });

        const isAiEliminateUsed = isAIEliminateUsed(
            layer,
            this.framework.getInstance(EliminatePenController).commercialFunc,
        );

        return {
            url: newURL,
            trackFunc,
            isAiEliminateUsed,
        };
    }

    /** 用于设置消除笔功能使用的埋点 */
    setTrackFunc(trackFunc: string, isReset = false) {
        if (isReset) {
            this.framework.getInstance(EliminatePenController).applyTrackFuncList = trackFunc ? [trackFunc] : [];
            return;
        }

        if (trackFunc) {
            this.framework.getInstance(EliminatePenController).applyTrackFuncList = [
                ...new Set([...this.framework.getInstance(EliminatePenController).applyTrackFuncList, trackFunc]),
            ];
        }
    }

    // 对外暴露的事件
    /**
     * 画笔绘制结束后的回调函数，通常用于埋点上报
     * @param callback -
     * @returns
     */
    onDrawEnd(callback: () => void) {
        const handler = this.framework.events.on(ELIMINATE_PEN_EVENT.DRAW_END, callback);

        return () => {
            this.framework.events.off(handler);
        };
    }

    /**
     * 应用消除效果后的回调函数，通常用于上报埋点
     * @param callback
     * @returns
     */
    onApplyEnd(callback: (trackList: string[]) => void) {
        const handler = this.framework.events.on(ELIMINATE_PEN_EVENT.APPLY_END, () => {
            callback(this.framework.getInstance(EliminatePenController).applyTrackFuncList);
            // 把应用消除的埋点数据，统一写入到确认消除埋点列表
            this.framework.getInstance(EliminatePenController).confirmTrackFuncList = [
                ...new Set([
                    ...this.framework.getInstance(EliminatePenController).confirmTrackFuncList,
                    ...this.framework.getInstance(EliminatePenController).applyTrackFuncList,
                ]),
            ];
            // 应用结束后，重置埋点
            this.setTrackFunc('', true);
        });

        return () => {
            this.framework.events.off(handler);
        };
    }

    /**
     * 用于监听消除笔 loading 状态
     * @param callback -
     * @returns
     */
    onLoading(callback: (value: boolean) => void) {
        const handler = this.framework.events.on(ELIMINATE_PEN_EVENT.LOADING, callback);

        return () => {
            this.framework.events.off(handler);
        };
    }
}
